/**
 * HrPulsePage — /admin/hr-pulse
 *
 * HR-focused rollup of artist activity:
 *  • Average hours/day per artist (calendar day vs active day)
 *  • Attendance % (days with >=30 min activity)
 *  • Longest absence streak in window
 *
 * Different lens from /admin/kpi-cockpit (which is operations-focused on
 * effort distribution). Here we care about presence, consistency,
 * burnout signals (e.g., 12+ hour days).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UserCheck, Calendar, AlertTriangle, RefreshCw, Filter, Heart } from 'lucide-react';

function attendanceColor(pct) {
  if (pct >= 80) return 'text-emerald-300 bg-emerald-500/10';
  if (pct >= 60) return 'text-amber-300 bg-amber-500/10';
  if (pct >= 40) return 'text-orange-300 bg-orange-500/10';
  return 'text-rose-300 bg-rose-500/10';
}

function burnoutFlag(hoursPerDay) {
  if (hoursPerDay >= 11) return { tone: 'rose', text: 'Burnout risk' };
  if (hoursPerDay >= 9)  return { tone: 'amber', text: 'High effort' };
  return null;
}

export default function HrPulsePage() {
  const { api, user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/admin/hr-pulse?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load HR data');
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="hr-pulse-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Heart className="w-6 h-6 text-rose-400" />
              HR Pulse
            </h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              People-side metrics for HR. Attendance, average hours, longest absence —
              and burnout flags when someone consistently exceeds 9–11 hr/day.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              data-testid="hr-pulse-days"
              className="bg-zinc-900 border border-zinc-800 rounded-md text-[12px] px-2 py-1"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchData}
              data-testid="hr-pulse-refresh"
              className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-zinc-900 border border-zinc-800 hover:bg-zinc-800"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-2 mb-6 text-[13px]">{error}</div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <Stat icon={UserCheck} label="Artists tracked" value={data?.summary?.user_count ?? 0} hint="With activity in window" />
          <Stat icon={Calendar}  label="Avg attendance" value={`${(data?.summary?.avg_attendance_pct ?? 0).toFixed(1)}%`} hint="Days worked / calendar days" />
          <Stat icon={Heart}     label="Avg hours/day" value={(data?.summary?.avg_hours_per_day ?? 0).toFixed(1)} hint="Per calendar day" />
        </div>

        {/* Per-artist table */}
        {!data || data.users.length === 0 ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
            <UserCheck className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">No artists tracked yet.</p>
            <p className="text-[11px] text-zinc-600 mt-1">Install the VOS Studio Agent on each artist's workstation to populate this view.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-800 overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-zinc-900/80 text-[11px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Artist</th>
                  <th className="text-left px-4 py-2 font-medium">Role</th>
                  <th className="text-right px-4 py-2 font-medium">Attendance</th>
                  <th className="text-right px-4 py-2 font-medium">Total hours</th>
                  <th className="text-right px-4 py-2 font-medium">Avg hr / active day</th>
                  <th className="text-right px-4 py-2 font-medium">Avg hr / cal day</th>
                  <th className="text-right px-4 py-2 font-medium">Longest gap</th>
                  <th className="text-left px-4 py-2 font-medium">Flag</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => {
                  const flag = burnoutFlag(u.avg_hours_per_active_day);
                  return (
                    <tr key={u.user_id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`hr-row-${u.user_id}`}>
                      <td className="px-4 py-2">
                        <div className="text-zinc-200 font-medium">{u.full_name || u.email || u.user_id.slice(0, 8)}</div>
                        <div className="text-[10px] text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-2 text-zinc-400 capitalize">{u.role}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono ${attendanceColor(u.attendance_pct)}`}>
                          {u.attendance_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-zinc-200 font-mono">{u.total_hours}h</td>
                      <td className="px-4 py-2 text-right text-zinc-300 font-mono">{u.avg_hours_per_active_day}h</td>
                      <td className="px-4 py-2 text-right text-zinc-400 font-mono">{u.avg_hours_per_calendar_day}h</td>
                      <td className="px-4 py-2 text-right text-zinc-400 font-mono">{u.longest_gap_days}d</td>
                      <td className="px-4 py-2">
                        {flag ? (
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${
                            flag.tone === 'rose'
                              ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                              : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                          }`}>
                            <AlertTriangle className="w-3 h-3" /> {flag.text}
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-zinc-600 mt-4">
          Privacy: HR Pulse reads aggregate counts only — never window titles, never keystrokes.
          Burnout flags are advisory, not punitive. Triggered when an artist's avg hr/active-day exceeds 9 (orange) or 11 (red).
        </p>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-rose-400" />
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>
    </div>
  );
}
